import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Folder, DotsThreeVertical, PencilSimple, Trash, ArrowRight } from '@phosphor-icons/react';
import type { DocumentNodeWithDetailsDto } from '@monorepo/shared';
import { DocumentNodeType } from '@monorepo/shared';
import { useListNodes } from '../../services/documents.service';
import { Tooltip } from '../ui/Tooltip';

interface ThemeCardProps {
  node: DocumentNodeWithDetailsDto;
  companyId: string;
  canRename?: boolean;
  canDelete?: boolean;
  onOpen: (nodeId: string) => void;
  onRename: (node: DocumentNodeWithDetailsDto) => void;
  onDelete: (node: DocumentNodeWithDetailsDto) => void;
}

const MAX_VISIBLE_SUBFOLDERS = 2;

export const ThemeCard = ({
  node,
  companyId,
  canRename = false,
  canDelete = false,
  onOpen,
  onRename,
  onDelete,
}: ThemeCardProps) => {
  const { t } = useTranslation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const { data: childrenData } = useListNodes(companyId, node.id);

  const subfolders = childrenData?.nodes.filter((n) => n.type === DocumentNodeType.FOLDER) ?? [];

  const subfoldersCount = subfolders.length;
  const visibleSubfolders = subfolders.slice(0, MAX_VISIBLE_SUBFOLDERS);
  const hasMoreSubfolders = subfoldersCount > MAX_VISIBLE_SUBFOLDERS;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleMenuToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsMenuOpen(!isMenuOpen);
  };

  const handleAction = (action: 'rename' | 'delete') => {
    setIsMenuOpen(false);
    if (action === 'rename') {
      onRename(node);
    } else if (action === 'delete') {
      onDelete(node);
    }
  };

  const handleSubfolderClick = (e: React.MouseEvent, subfolderId: string) => {
    e.stopPropagation();
    onOpen(subfolderId);
  };

  return (
    <div className="bg-white rounded-md p-3 shadow-[0px_4px_24px_0px_rgba(0,41,102,0.04)] flex flex-col gap-4 h-full">
      <div className="flex items-center justify-between gap-4">
        <div className="flex flex-col gap-0.5 flex-1 min-w-0">
          <Tooltip content={node.name} className="relative block min-w-0">
            <div className="flex items-center gap-2">
              <Folder size={20} className="text-primary flex-shrink-0" weight="regular" />
              <span className="text-sm font-medium text-gray-900 truncate">{node.name}</span>
            </div>
          </Tooltip>
          <span className="text-xs text-gray-500">
            ({t('documents.folder.subfolders_count', { count: subfoldersCount })})
          </span>
        </div>

        <div className="relative flex-shrink-0">
          <button
            ref={buttonRef}
            onClick={handleMenuToggle}
            className="p-2 rounded-md border border-gray-300 bg-white shadow-[0px_2px_10px_0px_rgba(0,41,102,0.08)] hover:bg-gray-50 transition"
          >
            <DotsThreeVertical size={20} className="text-gray-900" />
          </button>

          {isMenuOpen && (
            <div
              ref={menuRef}
              className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg py-1 z-10 min-w-[160px]"
            >
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setIsMenuOpen(false);
                  onOpen(node.id);
                }}
                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
              >
                <ArrowRight size={16} />
                {t('documents.actions.open')}
              </button>
              {canRename && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleAction('rename');
                  }}
                  className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                >
                  <PencilSimple size={16} />
                  {t('documents.actions.rename')}
                </button>
              )}
              {canDelete && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleAction('delete');
                  }}
                  className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                >
                  <Trash size={16} />
                  {t('documents.actions.delete')}
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="bg-background rounded-md p-2 flex-1">
        <div className="flex flex-col h-full">
          {visibleSubfolders.map((subfolder) => (
            <Tooltip key={subfolder.id} content={subfolder.name} className="relative block min-w-0">
              <button
                onClick={(e) => handleSubfolderClick(e, subfolder.id)}
                className="w-full flex items-center gap-2 px-0.5 py-2 text-left hover:bg-gray-100 rounded transition"
              >
                <Folder size={20} className="text-gray-500 flex-shrink-0" weight="regular" />
                <span className="text-sm font-medium text-gray-500 underline truncate">
                  {subfolder.name}
                </span>
              </button>
            </Tooltip>
          ))}
          {hasMoreSubfolders && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onOpen(node.id);
              }}
              className="flex items-center gap-2 px-0.5 py-2 text-left hover:bg-gray-100 rounded transition"
            >
              <span className="text-sm font-medium text-gray-500 underline">...</span>
            </button>
          )}
          {subfoldersCount === 0 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onOpen(node.id);
              }}
              className="flex-1 flex items-center justify-center py-4 text-sm text-gray-500 hover:bg-gray-100 rounded transition cursor-pointer"
            >
              {t('documents.empty.title')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
